import { toast } from '@/components/ui/sonner';
import { trackLeadConversion } from '@/lib/analytics';
import { ERROR_MESSAGES, normalizePhone } from '@/lib/submitLead';

const supabaseUrl = (process.env.REACT_APP_SUPABASE_URL || '').replace(/\/$/, '');
const ENDPOINT =
  process.env.REACT_APP_SUBMIT_BOOKING_ENQUIRY_URL ||
  (supabaseUrl ? `${supabaseUrl}/functions/v1/submit-booking-enquiry` : '');

const APIKEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_SUPABASE_ANON_KEY;

const PHONE_RE = /^\+[1-9]\d{7,14}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MAX_STAY_NIGHTS = 60;
const DAY_MS = 24 * 60 * 60 * 1000;

// Local date as YYYY-MM-DD (for <input type="date" min>).
export const todayISO = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const nightsBetween = (checkIn, checkOut) => {
  if (!DATE_RE.test(checkIn || '') || !DATE_RE.test(checkOut || '')) return null;
  const diff = (Date.parse(`${checkOut}T00:00:00Z`) - Date.parse(`${checkIn}T00:00:00Z`)) / DAY_MS;
  return Number.isFinite(diff) ? Math.round(diff) : null;
};

const clamp = (value, max) => String(value || '').trim().slice(0, max);

const fail = (code) => {
  toast.error(ERROR_MESSAGES[code] || 'Something went wrong. Please try again.');
  return { success: false, error: code };
};

export const submitBookingEnquiry = async (payload) => {
  if (!APIKEY) return fail('missing_apikey');
  if (!ENDPOINT) return fail('missing_endpoint');
  if (!payload?.consent) return fail('consent_required');

  const name = clamp(payload.name, 120);
  const phone = normalizePhone(payload.phone);
  const petName = clamp(payload.petName, 80);
  const petBreed = clamp(payload.petBreed, 80);
  const checkIn = String(payload.checkIn || '');
  const checkOut = String(payload.checkOut || '');

  if (!name) return fail('missing_name');
  if (!PHONE_RE.test(phone)) return fail('invalid_phone');
  if (!petName) return fail('missing_pet_name');
  if (!petBreed) return fail('missing_breed');

  const nights = nightsBetween(checkIn, checkOut);
  if (nights === null || checkIn < todayISO() || nights < 0 || nights > MAX_STAY_NIGHTS) {
    return fail('invalid_dates');
  }

  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: APIKEY,
      },
      body: JSON.stringify({ name, phone, petName, petBreed, checkIn, checkOut }),
    });

    let data = null;
    try {
      data = await res.json();
    } catch {
      return fail('internal_error');
    }

    if (data?.success) {
      trackLeadConversion('nest-booking-enquiry');
      toast.success("Thanks! We've received your booking enquiry — we'll call you shortly to confirm.");
      return data;
    }

    fail(data?.error || 'internal_error');
    return data || { success: false, error: 'internal_error' };
  } catch {
    return fail('network_error');
  }
};
